<template>
  <div class="modal-overlay comparison-modal">
    <div class="modal-container" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title d-flex align-items-center gap-3">
            <span>COMPARE QUOTES</span>
          </h5>
          <p class="d-flex align-items-center gap-1 mb-0">
            <span class="has_label_text">JID: </span>
            <CopyToClipboard :hasLabel="false" :copyValue="requestId" :checkValid="false" :label="''"  isHighlighted="true"/>
          </p>
          <p
            class="d-flex align-items-center mb-0 cursor-pointer" :class="isDownloadStart ? 'gap-3' : 'gap-1'" @click="handleQuotePDF" v-if="isSendQuotes">
            <span class="d-flex align-items-center">
              <Loading v-if="isDownloadStart" /> 
              <img class="w-4" src="@/assets/images/download-arw.png" v-else />
            </span>
            <span class="has_label_text">Send and Download Quotes</span>
          </p>
          <button class="close-button" @click="closemodal">
            <span>&times;</span>
          </button>
        </div>
        
        <div class="modal-body pt-0">
          <!-- Sticky Headers Row -->
          <div class="sticky-headers">
            <div class="feature-header-sticky">
              <div class="empty-logo-space">
                <p class="mb-0">Insurer</p>
              </div>
            </div>
            <div class="plan-headers-sticky">
              <div 
                v-for="(item, index) in compareList" 
                :key="index" 
                class="plan-header-sticky"
                :class="{ 
                  'highlight': index === 0,
                  'godigit-plan': isGoDigitComprehensive(item)
                }"
              >
                <div class="logo-container">
                  <img 
                    :src="resolveImagePath(item?.supportingData?.insCompanyLogo)" 
                    alt="Insurance Company Logo" 
                    class="company-logo" 
                    draggable="false" 
                  />
                </div>
              </div>
            </div>
          </div>
          
          <!-- Sticky Premium Row -->
          <div class="sticky-premium">
            <div class="feature-premium-sticky">
              <p class="mb-0 font-weight-bold">Final Premium (GST inc.)</p>
            </div>
            <div class="plan-premiums-sticky">
              <div 
                v-for="(item, index) in compareList" 
                :key="index" 
                class="plan-premium-sticky gap-2"
                :class="{ 
                  'highlight': index === 0,
                  'godigit-plan': isGoDigitComprehensive(item)
                }"
              >
                <span class="premium-amount">&#8377; {{ formatToINR(item.productDetails.totalPremiumDetails[0].totalPremiumWithTax) }}</span>
                <button @click="getSQ(item)" class="buy-button">
                  <span class="buy-text">Buy Now</span>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Scrollable Content -->
          <div class="comparison-table">
            <!-- Left column with labels -->
            <div class="feature-column">
              <div class="feature-item feature-name">
                <p>Plan Name</p>
              </div>
              
              <div class="feature-item">
                <p>IDV Cover</p>
              </div>
              
              <!-- Basic Cover section -->
              <div class="feature-item addon-header">
                <p class="font-weight-bold">Base Cover</p>
              </div>
              <div class="feature-item" v-if="checkPolicyTypeIsOwnDamage">
                <p>Basic Own Damage Premium</p>
              </div>

              <div class="feature-item" v-if="checkPolicyTypeIsThirdParty">
                <p>Basic Third Party Premium</p>
              </div>
                
              <template v-if="getBasicCoverAddons.length > 0">
                <div v-for="(addon, addonIndex) in getBasicCoverAddons" :key="addonIndex" class="feature-item addon-item">
                  <div class="addon-name-container">
                    <p>
                      {{ addon.optionDescription }} 
                        <span v-if="addon.optionSelected == 'paCvr'" class="si_value">(SI ₹ 15,00,000)</span>
                        <span v-else-if="addon.optionValue.length == 1 && addon.optionValue[0].optionValueInput != 0 && addon.optionSelected != 'tpCngCvr'" class="si_value">
                        (SI ₹ {{ formatIndianCurrency(addon.optionValue[0].optionValueInput) }})
                        </span>
                      </p>
                  </div>
                </div>
              </template>
              
              <!-- Addons section -->
              <template v-if="getAddonsSection.length > 0">
                <div class="feature-item addon-header">
                  <p class="font-weight-bold">Addons</p>
                </div>
                
                <div v-for="(addon, addonIndex) in getAddonsSection" :key="addonIndex" class="feature-item addon-item">
                  <div class="addon-name-container">
                    <p>{{ addon.optionDescription }}</p>
                  </div>
                </div>
              </template>
              
              <!-- Accessories section -->
              <template v-if="getAccessoriesSection.length > 0 && fuelType">
                <div class="feature-item addon-header">
                  <p class="font-weight-bold">Accessories</p>
                </div>
                
                <div v-for="(addon, addonIndex) in getAccessoriesSection" :key="addonIndex" class="feature-item addon-item">
                  <div class="addon-name-container">
                    <p>
                      {{ addon.optionDescription }}
                      <span class="si_value" v-if="!(fuelType == 'cng' || fuelType == 'lpg')">(SI ₹ {{ formatIndianCurrency(addon.optionValue[0].optionValueInput) }})</span>
                    </p>
                  </div>
                </div>
              </template>
              
              <!-- Discounts section -->
              <div class="feature-item addon-header" v-if="showDiscountSection">
                <p class="font-weight-bold">Discounts</p>
              </div>

              <div class="feature-item" v-if="checkPolicyTypeIsOwnDamage && !previousYearClaim && hasNcbDiscount">
                <p>No-Claim Bonus <span class="si_value">({{ ncbApplicablePercent }}%)</span></p>
              </div>
                
              <template v-if="getDiscountsSection.length > 0">
                <div v-for="(addon, addonIndex) in getDiscountsSection" :key="addonIndex" class="feature-item addon-item">
                  <div class="addon-name-container">
                    <p>{{ addon.optionDescription }}
                      <span v-if="addon.optionValue.length == 1 && addon.optionValue[0].optionValueInput != 0 && addon.optionSelected != 'payAsDrv'" class="si_value">
                        (SI ₹ {{ formatIndianCurrency(addon.optionValue[0].optionValueInput) }})
                      </span>
                      <span v-else-if="addon.optionValue.length == 1 && addon.optionValue[0].optionValueInput != 0" class="si_value">
                        (SI {{ formatIndianCurrency(addon.optionValue[0].optionValueInput) }} km/yr)
                      </span>
                    </p>
                  </div>
                </div>
              </template>
              

              <div class="feature-item addon-header">
                <p class="font-weight-bold">Premium Details</p>
              </div>

              <div class="feature-item">
                <p class="font-weight-bold">Package Premium</p>
              </div>
              <div class="feature-item">
                <p class="font-weight-bold">GST@18%</p>
              </div>
              <!-- <div class="feature-item">
                <p class="font-weight-bold">Final Premium (GST inc.)</p>
              </div> -->

              <div class="feature-item">
                <p class="d-flex align-items-center gap-1">
                  <img v-if="motorProductType == '4W-Pvt'" src="@/assets/images/inspection-icon.png" width="24"/> 
                  <img v-else src="@/assets/images/inspection-bike.png" width="24"/> 
                  <span>Inspection Required</span>
                </p>
              </div>
            </div>

            <!-- Insurance plan columns -->
            <div class="plans-container">
              <div 
                v-for="(item, index) in compareList" 
                :key="index" 
                class="plan-column"
                :class="{ 
                  'highlight': index === 0,
                  'godigit-plan': isGoDigitComprehensive(item)
                }"
              >
                <div class="plan-item plan-name">
                  <p>{{ item.productDetails.planOption.planName }}</p>
                </div>
                
                <div class="plan-item">
                  <p class="value">
                    {{
                      item.supportingData.insuredDeclaredValue.suggestedIDV &&
                      !['StandaloneTP(1)', 'StandaloneTP(3)', 'StandaloneTP(5)'].includes(item.insuranceAndProducts.policyType)
                        ? '₹ ' + formatIndianCurrency(item.supportingData.insuredDeclaredValue.suggestedIDV)
                        : 'NA'
                    }}
                  </p>
                </div>
                
                <!-- Basic Cover section -->
                <div class="plan-item addon-header">
                  <p class="value"><span style="visibility: hidden;">Base Cover</span></p>
                </div>
                <div class="plan-item" v-if="checkPolicyTypeIsOwnDamage">
                  <p class="value">{{
                    item.productDetails.basicOwnDamagePremiumForEachPolicyTerm.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF[0].totalBasicODPremiumValue && (item.insuranceAndProducts.policyType !=
                      'StandaloneTP(1)' || item.insuranceAndProducts.policyType !='Package(1+1)') ? currency(item.productDetails.basicOwnDamagePremiumForEachPolicyTerm.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF[0].basicODPremiumValue) :
                      'NA'
                  }}</p>
                </div>
                
                <div class="plan-item" v-if="checkPolicyTypeIsThirdParty">
                  <p class="value">{{
                    item.productDetails.thirdPartyPremiumForEachPolicyTerm.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF[0].basicTPPremiumValue && (item.insuranceAndProducts.policyType != 'StandaloneOD(1)' || item.insuranceAndProducts.policyType != 'Package(1+1)') ? currency(item.productDetails.thirdPartyPremiumForEachPolicyTerm.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF[0].basicTPPremiumValue) : 'NA'
                  }}</p>
                </div>

                <template v-if="getBasicCoverAddons.length > 0">
                  <div 
                    v-for="(addon, addonIndex) in getBasicCoverAddons" 
                    :key="addonIndex" 
                    class="plan-item addon-item"
                  >
                    <p class="addon-value">
                      <!-- Case 1: Plan has this addon -->
                      <template v-if="hasAddon(item, addon.optionSelected)">
                        <!-- Case 1C: Addon is included in plan premium -->
                        <span v-if="isPremiumIncluded(item, addon.optionSelected)" class="included-in-plan">
                          Included in Plan
                        </span>
                        <!-- Case 1A: Addon has premium value -->
                        <span v-else-if="getPremiumValue(item, addon.optionSelected) !== '0.00'" class="addon-price">{{ currency(getPremiumValue(item, addon.optionSelected)) }}
                        </span>
                        <!-- Case 1B: Addon is bundled -->
                        <span v-else-if="isBundledAddon(item, addon.optionSelected)" class="bundled-tick">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bundled-icon">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        </span>
                        <!-- Case 1D: Addon is not available -->
                        <span v-else class="addon-unavailable">
                          <img src="https://img.icons8.com/3d-fluency/94/delete-sign.png" alt="Not Available" class="img-cross" />
                        </span>
                      </template>
                      
                      <!-- Case 2: Bundled addon in GoDigit plans -->
                      <template v-else-if="addon.isBundled && isGoDigitComprehensive(item)">
                        <span class="bundled-tick">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bundled-icon">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        </span>
                      </template>
                      
                      <!-- Case 3: Not available for this plan -->
                      <span v-else class="addon-unavailable">
                        <img src="https://img.icons8.com/3d-fluency/94/delete-sign.png" alt="Not Available" class="img-cross" />
                      </span>
                    </p>
                  </div>
                </template>
                
                <!-- Addons section -->
                <template v-if="getAddonsSection.length > 0">
                  <div class="plan-item addon-header">
                    <p class="value"><span style="visibility: hidden;">Addons</span></p>
                  </div>
                  
                  <div 
                    v-for="(addon, addonIndex) in getAddonsSection" 
                    :key="addonIndex" 
                    class="plan-item addon-item"
                  >
                    <p class="addon-value">
                      <!-- Case 1: Plan has this addon -->
                      <template v-if="hasAddon(item, addon.optionSelected)">
                        <!-- Case 1C: Addon is included in plan premium -->
                        <span v-if="isPremiumIncluded(item, addon.optionSelected)" class="included-in-plan">
                          Included in Plan
                        </span>
                        <!-- Case 1A: Addon has premium value -->
                        <span v-else-if="getPremiumValue(item, addon.optionSelected) !== '0.00'" class="addon-price">{{ currency(getPremiumValue(item, addon.optionSelected)) }}
                        </span>
                        <!-- Case 1B: Addon is bundled -->
                        <span v-else-if="isBundledAddon(item, addon.optionSelected)" class="bundled-tick">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bundled-icon">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        </span>
                        <!-- Case 1D: Addon is not available -->
                        <span v-else class="addon-unavailable">
                          <img src="https://img.icons8.com/3d-fluency/94/delete-sign.png" alt="Not Available" class="img-cross" />
                        </span>
                      </template>
                      
                      <!-- Case 2: Bundled addon in GoDigit plans -->
                      <template v-else-if="addon.isBundled && isGoDigitComprehensive(item)">
                        <span class="bundled-tick">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bundled-icon">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        </span>
                      </template>
                      
                      <!-- Case 3: Not available for this plan -->
                      <span v-else class="addon-unavailable">
                        <img src="https://img.icons8.com/3d-fluency/94/delete-sign.png" alt="Not Available" class="img-cross" />
                      </span>
                    </p>
                  </div>
                </template>
                
                <!-- Accessories section -->
                <template v-if="getAccessoriesSection.length > 0">
                  <div class="plan-item addon-header">
                    <p class="value"><span style="visibility: hidden;">Accessories</span></p>
                  </div>
                  
                  <div 
                    v-for="(addon, addonIndex) in getAccessoriesSection" 
                    :key="addonIndex" 
                    class="plan-item addon-item"
                  >
                    <p class="addon-value">
                      <!-- Case 1: Plan has this addon -->
                      <template v-if="hasAddon(item, addon.optionSelected)">
                        <!-- Case 1C: Addon is included in plan premium -->
                        <span v-if="isPremiumIncluded(item, addon.optionSelected)" class="included-in-plan">
                          Included in Plan
                        </span>
                        <!-- Case 1A: Addon has premium value -->
                        <span v-else-if="getPremiumValue(item, addon.optionSelected) !== '0.00'" class="addon-price">{{ currency(getPremiumValue(item, addon.optionSelected)) }}
                        </span>
                        <!-- Case 1B: Addon is bundled -->
                        <span v-else-if="isBundledAddon(item, addon.optionSelected)" class="bundled-tick">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bundled-icon">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        </span>
                        <!-- Case 1D: Addon is not available -->
                        <span v-else class="addon-unavailable">
                          <img src="https://img.icons8.com/3d-fluency/94/delete-sign.png" alt="Not Available" class="img-cross" />
                        </span>
                      </template>
                      
                      <!-- Case 2: Bundled addon in GoDigit plans -->
                      <template v-else-if="addon.isBundled && isGoDigitComprehensive(item)">
                        <span class="bundled-tick">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bundled-icon">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        </span>
                      </template>
                      
                      <!-- Case 3: Not available for this plan -->
                      <span v-else class="addon-unavailable">
                        <img src="https://img.icons8.com/3d-fluency/94/delete-sign.png" alt="Not Available" class="img-cross" />
                      </span>
                    </p>
                  </div>
                </template>
                
                <!-- Discounts section -->
                <div class="plan-item addon-header" v-if="showDiscountSection">
                  <p class="value"><span style="visibility: hidden;">Discounts</span></p>
                </div>

                <div class="plan-item"  v-if="checkPolicyTypeIsTP && !previousYearClaim && hasNcbDiscount">
                  <p class="value">- {{ currency(item.productDetails?.motorNCBPremium[0].premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF[0].premiumValue)}}</p>
                </div>
                  
                <template v-if="getDiscountsSection.length > 0">
                  <div 
                    v-for="(addon, addonIndex) in getDiscountsSection" 
                    :key="addonIndex" 
                    class="plan-item addon-item"
                  >
                    <p class="addon-value">
                      <!-- Case 1: Plan has this addon -->
                      <template v-if="hasAddon(item, addon.optionSelected)">
                        <!-- Case 1C: Addon is included in plan premium -->
                        <span v-if="isPremiumIncluded(item, addon.optionSelected)" class="included-in-plan">
                          Included in Plan
                        </span>
                        <!-- Case 1A: Addon has premium value -->
                        <span v-else-if="getPremiumValue(item, addon.optionSelected) !== '0.00'" class="addon-price">- {{ currency(getPremiumValue(item, addon.optionSelected)) }}
                        </span>
                        <!-- Case 1B: Addon is bundled -->
                        <span v-else-if="isBundledAddon(item, addon.optionSelected)" class="bundled-tick">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bundled-icon">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        </span>
                        <!-- Case 1D: Addon is not available -->
                        <span v-else class="addon-unavailable">
                          <img src="https://img.icons8.com/3d-fluency/94/delete-sign.png" alt="Not Available" class="img-cross" />
                        </span>
                      </template>
                      
                      <!-- Case 2: Bundled addon in GoDigit plans -->
                      <template v-else-if="addon.isBundled && isGoDigitComprehensive(item)">
                        <span class="bundled-tick">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bundled-icon">
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                        </span>
                      </template>
                      
                      <!-- Case 3: Not available for this plan -->
                      <span v-else class="addon-unavailable">
                        <img src="https://img.icons8.com/3d-fluency/94/delete-sign.png" alt="Not Available" class="img-cross" />
                      </span>
                    </p>
                  </div>
                </template>

                <div class="plan-item addon-header">
                  <p class="value"><span style="visibility: hidden;">Premium Details</span></p>
                </div>

                <div class="plan-item">
                  <p class="value">{{ currency(item.productDetails?.totalPremiumDetails[0].totalPremiumValueBeforeTax)}}</p>
                </div>

                <div class="plan-item">
                  <p class="value">{{ currency(item.productDetails?.totalPremiumDetails[0].totalTax)}}</p>
                </div>

                <!-- <div class="plan-item">
                  <p class="value">&#8377; {{ currency(item.productDetails?.totalPremiumDetails[0].totalPremiumWithTax)}}</p>
                </div> -->
                <div class="plan-item">
                  <p 
                    class="value" 
                    :class="item.productDetails.inspectionDetails.isInspectionRequired ? 'inspection-required' : 'not-inspection-required'"
                  >
                    {{ item.productDetails.inspectionDetails.isInspectionRequired ? "YES" : "NA" }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CopyToClipboard from "./../components/uiResuable/CopyToClipBoard.vue"
import Loading from "@/components/uiResuable/Loading.vue"
export default {
  props: ["compareList", "motorCovers", "requestId", "isDownloadStart", "isSendQuotes"],
  components: {
    CopyToClipboard,
    Loading,
  },
  data() {
    return {
      fuelType: "",
      previousYearClaim: false
    }
  },
  mounted() {
    if(sessionStorage.getItem("userData")){
      let userData = JSON.parse(sessionStorage.getItem("userData"));
      this.fuelType = userData.fuelType.toLowerCase();
      this.previousYearClaim = userData.previousPolicyExpired == 'false' ? false : true;
     }
  },
  methods: {
    premiumRound(amount){
      return Math.round(amount);
    },
    closemodal() {
      this.$emit('closeCompareQuote')
    },
    getSQ(data) {
      this.$emit('closeCompareQuote')
      this.$emit('getPolicydetails', data)
    },
    resolveImagePath(variable) {
      return variable || "";
    },
    handleQuotePDF(){
      this.$emit('handleQuotePDF', this.compareList, true);
    },
    getClaimPercentage(item) {
      try {
        // Check if claimSettlementPercent exists
        if (item.supportingData.Messages && item.supportingData.Messages.claimSettlementPercent) {
          // Try parsing the JSON
          const claimSettlementData = JSON.parse(item.supportingData.Messages.claimSettlementPercent);
          
          // If the parse is successful and has the expected field, return it
          if (claimSettlementData && claimSettlementData["2021-2022"]) {
            return claimSettlementData["2021-2022"] + '%';
          }
        }
        // Default return if any condition fails
        return 'NA';
      } catch (error) {
        // If JSON.parse fails, return NA
        console.warn("Error parsing claim percentage data:", error);
        return 'NA';
      }
    },
    getPremiumValue(item, addonOption) {
      // Find the addon in the item's motorCovers
      const addon = item.productDetails.motorCovers.find(cover => 
        cover.optionSelected === addonOption
      );
      
      // Return premium if addon exists and has valid pricing structure
      if (addon && 
          addon.premiumForEachPolicyTerm && 
          addon.premiumForEachPolicyTerm[0] && 
          addon.premiumForEachPolicyTerm[0].premiumForEachPPO && 
          addon.premiumForEachPolicyTerm[0].premiumForEachPPO[0] && 
          addon.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF && 
          addon.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF[0] &&
          addon.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF[0].premiumValue) {
        return addon.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF[0].premiumValue.toString();
      }
      
      return "0.00";
    },
    // Check if a plan is GoDigit comprehensive
    isGoDigitComprehensive(item) {
      const productCode = item.insuranceAndProducts?.productCode;
      return productCode && productCode.startsWith("GO") && productCode !== 'GO4WTP';
    },
    // Check if plan has the specific addon
    hasAddon(item, optionSelected) {
      return item.productDetails.motorCovers.some(cover => 
        cover.optionSelected === optionSelected
      );
    },
    // Check if an addon is a bundled addon (inbuilt in plan)
    isBundledAddon(item, optionSelected) {
      const matchingAddon = item.productDetails.motorCovers.find(cover => 
        cover.optionSelected === optionSelected
      );
      
      return matchingAddon && matchingAddon.inbuiltInPlan === "Yes";
    },
    // Check if an addon is included in plan premium
    isPremiumIncluded(item, optionSelected) {
      const addon = item.productDetails.motorCovers.find(cover => 
        cover.optionSelected === optionSelected
      );
      
      return addon && (addon.premiumIncludedFlag === "1" || addon.premiumIncludedFlag === "Yes");
    },
    // Check if all addons in a row are unavailable across all plans
    isAddonRowCompletelyUnavailable(addon) {
      return this.compareList.every(item => {
        // Check if plan has this addon
        if (this.hasAddon(item, addon.optionSelected)) {
          // If addon is included in plan premium, it's available
          if (this.isPremiumIncluded(item, addon.optionSelected)) {
            return false;
          }
          // If addon has premium value, it's available
          if (this.getPremiumValue(item, addon.optionSelected) !== '0.00') {
            return false;
          }
          // If addon is bundled, it's available
          if (this.isBundledAddon(item, addon.optionSelected)) {
            return false;
          }
          // Otherwise it's unavailable for this plan
          return true;
        }
        
        // Check if it's a bundled addon in GoDigit plans
        if (addon.isBundled && this.isGoDigitComprehensive(item)) {
          return false; // Available
        }
        
        // Not available for this plan
        return true;
      });
    },
    // Format currency in Indian format
    formatIndianCurrency(value) {
      // If value is already a string that's formatted, return it
      if (typeof value === 'string' && value.includes(',')) {
        return value;
      }
      
      // Ensure we're working with a number
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      
      // Format the number with commas for thousands
      return numValue.toLocaleString('en-IN');
    },
    currency(amt) {
      if(!amt) return 'NA'
      const tmpAmt = parseFloat(amt).toFixed(2);
      return '₹ '+tmpAmt.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    // Get selected addons from motorCovers
    getSelectedAddons() {
      return this.motorCovers
        .filter((ele) => ele.optionSelected)
        .map((ele) => ({
          optionSelected: ele.optionSelected,
          optionValue: ele.optionValue,
        }));
    },
  },
  computed: {
    // Find the GoDigit plan in compareList
    baseGoDigitPlan() {
      return this.compareList.find(plan => this.isGoDigitComprehensive(plan)) || this.compareList[0];
    },
    motorProductType() {
      return this.$store.state.motorProductType;
    },
    // Get all addons from GoDigit plan for comparison
    getAllAddons() {
      // Start with user selected addons
      const selectedAddons = new Set(this.getSelectedAddons().map(addon => addon.optionSelected));

      const addonMap = new Map();
      this.compareList && this.compareList.forEach((ele) => {
        if (ele && ele.productDetails && ele.productDetails.motorCovers) {
          ele.productDetails.motorCovers.forEach((addon) => {
            // Skip Legal Liability to Employees if any issues with it
            if (
              addon.optionSelected === "legLibEm" &&
              (!addon.premiumForEachPolicyTerm ||
                !addon.premiumForEachPolicyTerm[0] ||
                !addon.premiumForEachPolicyTerm[0].premiumForEachPPO ||
                !addon.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF ||
                !addon.premiumForEachPolicyTerm[0].premiumForEachPPO[0].premiumForEachPPF[0].totalPremiumValue)
            ) {
              return;
            }

            // Use optionSelected as unique key (can also use combo key if needed)
            if (!addonMap.has(addon.optionSelected)) {
              addonMap.set(addon.optionSelected, {
                optionSelected: addon.optionSelected,
                optionDescription: addon.optionDescription,
                isSelected: selectedAddons.has(addon.optionSelected),
                policyType: ele.insuranceAndProducts.policyType,
                optionValue: addon.optionValue,
              });
            }
          });
        }
      });
      return Array.from(addonMap.values());;
    },
    // Get Basic Cover addons
    getBasicCoverAddons() {
      const basicCoverOptions = this.getPremiumBreakup.basic_cover;
      const data = this.getAllAddons.filter(addon => 
        basicCoverOptions.includes(addon.optionSelected) && 
        !this.isAddonRowCompletelyUnavailable(addon)
      ).sort((a, b) => {
        const aIndex = basicCoverOptions.indexOf(a.optionSelected);
        const bIndex = basicCoverOptions.indexOf(b.optionSelected);
        return aIndex - bIndex;
      });
      return data;
    },
    // Get Addons section
    getAddonsSection() {
      const addonsOptions = this.getPremiumBreakup.Addons;
      return this.getAllAddons.filter(addon => 
        addonsOptions.includes(addon.optionSelected) && 
        !this.isAddonRowCompletelyUnavailable(addon)
      ).sort((a, b) => {
        const aIndex = addonsOptions.indexOf(a.optionSelected);
        const bIndex = addonsOptions.indexOf(b.optionSelected);
        return aIndex - bIndex;
      });
    },
    // Get Accessories section
    getAccessoriesSection() {
      const accessoriesOptions = this.getPremiumBreakup.Accessories;
      return this.getAllAddons.filter(addon => 
        accessoriesOptions.includes(addon.optionSelected) && 
        !this.isAddonRowCompletelyUnavailable(addon)
      ).sort((a, b) => {
        const aIndex = accessoriesOptions.indexOf(a.optionSelected);
        const bIndex = accessoriesOptions.indexOf(b.optionSelected);
        return aIndex - bIndex;
      });
    },
    // Get Discounts section
    getDiscountsSection() {
      const discountOptions = this.getPremiumBreakup.Discount;
      return this.getAllAddons.filter(addon => 
        discountOptions.includes(addon.optionSelected) && 
        !this.isAddonRowCompletelyUnavailable(addon)
      ).sort((a, b) => {
        const aIndex = discountOptions.indexOf(a.optionSelected);
        const bIndex = discountOptions.indexOf(b.optionSelected);
        return aIndex - bIndex;
      });
    },
    checkPolicyTypeIsThirdParty(){
      return this.compareList[0].insuranceAndProducts.policyTypeDesc?.toLowerCase() == 'third party' || this.compareList[0].insuranceAndProducts.policyTypeDesc?.toLowerCase() == 'comprehensive'
    },
    checkPolicyTypeIsOwnDamage(){
      return this.compareList[0].insuranceAndProducts.policyTypeDesc?.toLowerCase() == 'own damage' || this.compareList[0].insuranceAndProducts.policyTypeDesc?.toLowerCase() == 'comprehensive'
    },
    checkPolicyTypeIsTP(){
      return this.compareList[0].insuranceAndProducts.policyTypeDesc?.toLowerCase() !== 'third party'
    },
    hasNcbDiscount() {
      return this.compareList?.some((item) => {
        const ncbPremium = item?.productDetails?.motorNCBPremium?.[0]?.premiumForEachPolicyTerm?.[0]?.premiumForEachPPO?.[0]?.premiumForEachPPF?.[0]?.premiumValue;
        return Number(ncbPremium) > 0;
      });
    },
    showDiscountSection() {
      return this.checkPolicyTypeIsTP && ((this.hasNcbDiscount && !this.previousYearClaim) || this.getDiscountsSection.length > 0);
    },
    ncbApplicablePercent() {
      const planWithNcbPercent = this.compareList?.find((item) => {
        const ncbPercent = item?.productDetails?.motorNCBPremium?.[0]?.nextNcbSlabDetails?.[0]?.nextNcbSlabApplicable;
        return ncbPercent !== undefined && ncbPercent !== null && `${ncbPercent}`.trim() !== "";
      });
      const ncbPercent = planWithNcbPercent?.productDetails?.motorNCBPremium?.[0]?.nextNcbSlabDetails?.[0]?.nextNcbSlabApplicable;
      return `${ncbPercent ?? 0}`.trim();
    },
    getPremiumBreakup() {
      return this.$store.state.configData && this.$store.state.configData.PREMIUM_BREAKUP ? this.$store.state.configData.PREMIUM_BREAKUP : {};
    }
  },
}
</script>

<style lang="scss" scoped>
@import "../assets/scss/distributorVariables/variable.scss";
@import "../assets/scss/distributorVariables/_common.scss";

// Modal overlay and container
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  z-index: 1000;
}

.modal-container {
  max-width: 90%;
  width: 1000px;
  max-height: 90vh;
  background-color: #fff;
  border-radius: 16px;
  // overflow: hidden;
  position: relative;
  animation: modal-slide-in 0.3s ease;
}

@keyframes modal-slide-in {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

// Modal content
.modal-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

// Modal header
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  border-bottom: 1px solid #eaeaea;
  background-color: #f8f9fa;
  border-radius: 12px 12px 0 0;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: $base-text;
  margin: 0;
  p{
    font-size: 15px !important;
  }
}

.close-button {
  background: transparent;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: $base-text;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

// Modal body
.modal-body {
  padding: 24px;
  overflow-y: auto;
  max-height: 80vh;
  position: relative;
}

// Sticky Headers
.sticky-headers {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: #fff;
  display: flex;
  border-bottom: 2px solid #eaeaea;
}

.feature-header-sticky {
  width: 32%;
  flex-shrink: 0;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-right: 1px solid #eaeaea;
  padding: 16px;
}

.plan-headers-sticky {
  display: flex;
  overflow-x: auto;
  flex-grow: 1;
  scrollbar-width: thin;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d1d1;
    border-radius: 3px;
  }
}

.plan-header-sticky {
  min-width: 200px;
  flex: 1;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #eaeaea;
  padding: 16px;
  background-color: #fff;
  
  &:last-child {
    border-right: none;
  }
  
  &.godigit-plan {
    border-bottom-color: rgba(46, 204, 113, 0.2);
    // background-color: rgba(46, 204, 113, 0.02);
  }
  
  .logo-container {
    width: 100%;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
  
  .company-logo {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

// Sticky Premium Row
.sticky-premium {
  position: sticky;
  top: 120px;
  z-index: 100;
  background-color: #fff;
  display: flex;
  border-bottom: 2px solid #eaeaea;
}

.feature-premium-sticky {
  width: 32%;
  flex-shrink: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f9fa;
  border-right: 1px solid #eaeaea;
  padding: 16px;
  .font-weight-bold{
    font-weight: 600;
    font-size: 14px;
  }
}

.plan-premiums-sticky {
  display: flex;
  overflow-x: auto;
  flex-grow: 1;
  scrollbar-width: thin;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d1d1;
    border-radius: 3px;
  }
}

.plan-premium-sticky {
  min-width: 200px;
  flex: 1;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-right: 1px solid #eaeaea;
  padding: 16px;
  background-color: #fff;
  
  &:last-child {
    border-right: none;
  }
  
  &.godigit-plan {
    border-bottom-color: rgba(46, 204, 113, 0.2);
    // background-color: rgba(46, 204, 113, 0.02);
  }
  
  .buy-button {
    max-width: 100px;
    padding: 0.5rem;
    background: $main-bg;
    color: #FFF;
    border: none;
    border-radius: 50px;
    width: 100%;
    transition: all 0.3s ease;
    cursor: pointer;
    flex-wrap: wrap;
    justify-content: space-between;
    
    &:hover {
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    .premium-amount {
      font-size: 12px;
      font-weight: 600;
      margin-bottom: 0;
    }
    
    .buy-text {
      font-size: 13px;
      opacity: 0.9;
      position: relative;
    }
    
    &:hover .buy-text:after {
      opacity: 1;
      transform: translateX(0);
    }
  }
}

// Comparison table
.comparison-table {
  display: flex;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

// Feature column (left side)
.feature-column {
  width: 32%;
  flex-shrink: 0;
  border-right: 1px solid #eaeaea;
  display: flex;
  flex-direction: column;
}

.empty-logo-space {
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
}

.feature-item {
  padding: 16px 20px;
  border-bottom: 1px solid #eaeaea;
  background-color: #f8f9fa;
  height: 60px; /* Fixed height for better alignment */
  display: flex;
  align-items: center;
  
  p {
    margin: 0;
    font-size: 14px;
    color: #495057;
    opacity: 1;
  }
  .font-weight-bold{
    font-weight: 600;
  }
}

.feature-name {
  min-height: 70px;
  height: 70px; /* Consistent height with plan-name */
}

.addon-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #eaeaea;
}

// Addon name container with badge
.addon-name-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  
  p {
    margin-right: 8px;
    flex-grow: 1;
  }
}

.selected-badge {
  background-color: #2ecc71;
}

.addon-items {
  display: flex;
  flex-direction: column;
  
  .feature-item, .plan-item {
    min-height: 50px;
    height: 50px; /* Fixed height for addon items */
    display: flex;
    align-items: center;
  }
  
  .addon-item p {
    font-weight: 400;
    font-size: 13px;
  }
}

// Plans container (right side with scrollable plans)
.plans-container {
  display: flex;
  overflow-x: auto;
  flex-grow: 1;
  scrollbar-width: thin;
  position: relative;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d1d1;
    border-radius: 3px;
  }
}

// Individual plan column
.plan-column {
  min-width: 200px;
  flex: 1;
  border-right: 1px solid #eaeaea;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    z-index: 2;
  }
  
  &.highlight {
    z-index: 1;
  }
  
  &.godigit-plan {
    z-index: 3;
    
    .plan-item {
      border-bottom-color: rgba(46, 204, 113, 0.2);
    }
  }
}

.plan-item {
  padding: 16px;
  border-bottom: 1px solid #eaeaea;
  height: 60px; /* Fixed height for consistent row alignment */
  display: flex;
  align-items: center;
  justify-content: center;
  
  p {
    margin: 0;
    text-align: center;
    font-size: 14px;
    color: #0bc276;
    opacity: 1;
  }
  
  .value {
    font-weight: 500;
  }
}

.plan-name {
  min-height: 70px;
  height: 70px; /* Consistent height */
  
  p {
    font-weight: 600;
    font-size: 15px;
    color: #2c3e50;
  }
}

// Addon styling
.addon-value {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.addon-spacer {
  height: 50px; /* Match addon item height */
}

.addon-price {
  font-weight: 500;
  color: #28a745;
}

.included-in-plan {
  font-size: 12px;
  font-weight: 500;
  color: $main-bg;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid $main-bg;
}

.addon-unavailable {
  display: flex;
  align-items: center;
  justify-content: center;
}

.img-cross {
  width: 18px;
  height: 18px;
  opacity: 0.7;
}

// Bundled addon checked icon
.bundled-tick {
  display: flex;
  align-items: center;
  justify-content: center;
  
  .bundled-icon {
    color: #4cd137;
    stroke-width: 3;
    animation: scale-in 0.3s ease;
  }
}

@keyframes scale-in {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

// Inspection status
.inspection-required {
  color: #27ae60 !important;
  font-weight: 600 !important;
}
.not-inspection-required {
  color: #dc3545 !important;
  font-weight: 600 !important;
}

// Dark theme support
.dark {
  .empty-logo-space{
    p{
      color: $heading-darkColor;
    }
  }
  .modal-container {
    background-color: $header-dark-bg;
    border:1px solid $heading-darkColor;
  }
  
  .modal-header {
    background-color: $header-dark-bg;
    
  }

  
.has_label_text{
  color: $commonColor !important;
}
  
  .modal-title, .close-button {
    color: $heading-darkColor;
  }
  
  .feature-column, .plan-column {
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .feature-item, .plan-item {
    background-color: transparent;
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .sticky-headers {
    background-color: $header-dark-bg;
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }
  
  .feature-header-sticky {
    background-color: $header-dark-bg;
    border-right-color: rgba(255, 255, 255, 0.1);
  }
  
  .plan-header-sticky {
    background-color: $header-dark-bg;
    border-right-color: rgba(255, 255, 255, 0.1);
    
    &.godigit-plan {
      // background-color: darken($header-dark-bg, 3%);
      border-bottom-color: rgba(39, 174, 96, 0.2);
    }
    
    .logo-container {
      background-color: $commonColor;
    }
  }
  
  .sticky-premium {
    background-color: $header-dark-bg;
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }
  
  .feature-premium-sticky {
    background-color: $header-dark-bg;
    border-right-color: rgba(255, 255, 255, 0.1);
    
    p {
      color: $heading-darkColor;
      font-weight: 600;
      font-size: 14px;
    }
  }
  
  .plan-premium-sticky {
    background-color: $header-dark-bg;
    border-right-color: rgba(255, 255, 255, 0.1);
    
    &.godigit-plan {
      // background-color: darken($header-dark-bg, 3%);
      border-bottom-color: rgba(39, 174, 96, 0.2);
    }
  }
  
  .feature-item p, .plan-item p {
    color: $heading-darkColor;
  }
  .plan-item .value{
    color: #27ae60;
  }
  
  .selected-badge {
    background-color: #27ae60;
  }
  
  .included-in-plan {
    color: #3498db;
    background-color: rgba(52, 152, 219, 0.15);
    border-color: rgba(52, 152, 219, 0.4);
  }
  
  .plan-column {    
    &.godigit-plan {
      // background-color: darken($header-dark-bg, 3%);
      box-shadow: 0 0 15px rgba(0, 155, 77, 0.2);
      .plan-item {
        border-bottom-color: rgba(39, 174, 96, 0.2);
      }
    }
  }
  .premium-amount{
    color: $commonColor;
  }
  
  .close_icon {
    filter: invert(1);
  }  
  .bundled-icon {
    color: #44bd32;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .modal-container {
    max-width: 95%;
    max-height: 95vh;
  }
  
  .feature-column {
    width: 45%;
  }
  
  .plan-column {
    min-width: 180px;
  }
  
  .modal-title {
    font-size: 18px;
  }
  
  .feature-item p, .plan-item p {
    font-size: 13px;
  }
  
  .buy-button {
    
    .premium-amount {
      font-size: 14px;
    }
    
    .buy-text {
      font-size: 11px;
    }
  }
  
  .included-in-plan {
    font-size: 10px;
    padding: 2px 6px;
  }
}
.cursor-pointer {
  cursor: pointer;
}
</style>